import { type ProductData } from "~/domains/product/types/ProductData";
import { DownloadUrlLink, getDownloadProperty } from "~/common/components/Link";
import { trackPDPDownloadDatasheet } from "~/app/analytics/ga/ga";
import { useUnitMode } from "~/domains/userPreferences/hooks/useUnitMode";
import { useTranslation } from "react-i18next";
import type { ReactNode, MouseEvent } from "react";
import type { UnitMode } from "~/domains/userPreferences/types/UnitMode";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { downloadBlobFromResponse } from "~/common/utils/ApiHelper";

interface PdfDownloadLinkProps {
  product: Pick<ProductData, "code" | "designation">;
  children?: ReactNode;
}

export const buildPdfDownloadOccUrl = (productCode: string, unitMode: UnitMode) => {
  let timezone;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    timezone = "UTC";
  }

  const params = new URLSearchParams();
  params.append("timezone", timezone);
  params.append("unit", unitMode);

  return occUrl(`/p/pdf/${productCode}`) + "?" + params.toString();
};

const PdfDownloadLink = ({ product }: PdfDownloadLinkProps) => {
  const { t } = useTranslation();

  const unitMode = useUnitMode();

  const label = t("productDetailPage.downloadPdf");

  const handleClick = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackPDPDownloadDatasheet();
    const url = buildPdfDownloadOccUrl(product.code, unitMode);
    const response = await occFetch(url);
    if (response.ok) {
      await downloadBlobFromResponse(response);
    }
  };

  return (
    <DownloadUrlLink
      download={getDownloadProperty(`${product.designation}.pdf`)}
      href={buildPdfDownloadOccUrl(product.code, unitMode)}
      className={"scha__pdp__pdf-link"}
      onClick={handleClick}
    >
      {label}
    </DownloadUrlLink>
  );
};

export default PdfDownloadLink;
